import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@ondit/ui";
import { useQuery } from "@tanstack/react-query";
import { CreditsStat } from "~/common/billing/topup";
import { api, type Org } from "~/core/api/index";
import * as fmt from "~/core/format";
import { useI18n, useT } from "~/core/i18n";
import { keys, read } from "~/core/query";
import { Section } from "~/core/ui/page";
import { Async } from "~/core/ui/states";

/**
 * What a ledger line was for.
 *
 * The reasons the server writes are a known, small set — a subscription grant,
 * one of the priced actions, a refund — but the column is an open string, and a
 * reason this panel has no sentence for is shown as it was stored rather than
 * as a bare missing-string key.
 */
function reasonLabel(t: (key: string) => string, reason: string): string {
	const label = t(`ledger.${reason}`);
	return label === `ledger.${reason}` ? reason : label;
}

/**
 * Where the credits went.
 *
 * The ledger is shown in full rather than as a balance, because "what did my
 * $49 buy" is the question this exists to answer, and a single number answers
 * it badly.
 *
 * Two tables, and the split is the one the customer is billed by: model usage
 * is real money spent at the model's published price, the ledger is credits.
 * Sampling appears in the first and not the second, which is why a balance that
 * has not moved can sit above a bill that has.
 */
export function UsagePanel({ org }: { org: Org }) {
	const t = useT();
	const { locale } = useI18n();
	const credits = useQuery({
		queryKey: [...keys.org(org.id), "credits"],
		queryFn: () => api.credits(org.id),
		...read,
	});
	const usage = useQuery({ queryKey: [...keys.org(org.id), "usage"], queryFn: () => api.usage(org.id), ...read });
	const lots = credits.data?.credits;

	return (
		<>
			{/* The number, in its two lifetimes — what lapses with the period and
			    what was bought — on the card the number is on, with the way to
			    add to the second under it. */}
			<div className="stats">
				<CreditsStat org={org} credits={lots} state={org.subscription_state} foot={t("billing.balance")} />
			</div>

			<Section title={t("billing.usage")}>
				<Async state={usage} empty={<p className="empty">{t("billing.usageEmpty")}</p>}>
					{(data) =>
						data.models.length === 0 ? (
							<p className="empty">{t("billing.usageEmpty")}</p>
						) : (
							<div className="table-scroll bleed-gutter" style={{ marginBottom: "var(--s-9)" }}>
								<Table variant="resource">
									<TableHeader>
										<TableRow>
											<TableHead>{t("billing.model")}</TableHead>
											<TableHead className="num">{t("billing.calls")}</TableHead>
											<TableHead className="num">{t("billing.tokens")}</TableHead>
											<TableHead className="num">{t("billing.spend")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{data.models.map((row) => (
											<TableRow key={row.model}>
												<TableCell>{row.model}</TableCell>
												<TableCell className="num">{fmt.number(locale, row.calls)}</TableCell>
												<TableCell className="num">
													{fmt.number(locale, row.input_tokens + row.output_tokens)}
												</TableCell>
												<TableCell className="num">{fmt.usd(locale, row.billed_micros)}</TableCell>
											</TableRow>
										))}
										<TableRow>
											<TableCell colSpan={3}>
												<strong>{t("billing.usageTotal", { days: String(data.days) })}</strong>
											</TableCell>
											<TableCell className="num">
												<strong>{fmt.usd(locale, data.total_micros)}</strong>
											</TableCell>
										</TableRow>
									</TableBody>
								</Table>
							</div>
						)
					}
				</Async>
			</Section>

			<Section title={t("billing.history")}>
				<Async state={credits} empty={<p className="empty">{t("billing.empty")}</p>}>
					{(data) =>
						data.entries.length === 0 ? (
							<p className="empty">{t("billing.empty")}</p>
						) : (
							<div className="table-scroll bleed-gutter">
								<Table variant="resource">
									<TableHeader>
										<TableRow>
											<TableHead>{t("table.reason")}</TableHead>
											<TableHead className="num">{t("table.change")}</TableHead>
											<TableHead className="num">{t("table.balance")}</TableHead>
											<TableHead>{t("table.date")}</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{data.entries.map((entry) => (
											<TableRow key={entry.id}>
												<TableCell>{reasonLabel(t, entry.reason)}</TableCell>
												<TableCell
													className="num mono"
													style={{ color: entry.delta > 0 ? "var(--success)" : undefined }}
												>
													{fmt.signed(locale, entry.delta)}
												</TableCell>
												<TableCell className="num mono">{entry.balance_after}</TableCell>
												<TableCell className="mono">{fmt.date(locale, entry.created_at)}</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						)
					}
				</Async>
			</Section>
		</>
	);
}
